/**
 * Vehicle catalog client service.
 *
 * Phase 0 (spike): we query catalog rows directly via PostGraphile's
 * auto-generated `allVehicleCatalogs` connection, then run a lightweight
 * matcher in the browser against the VIN decode result. This keeps the
 * create-listing flow responsive and avoids an extra round-trip.
 *
 * Phase 1 (once the full catalog is imported and we want server-side alias
 * management / weighting tuning), the matching can move into a PostgreSQL
 * function exposed through PostGraphile; the UI contract stays the same.
 */
import { gql, useQuery, useApolloClient } from "@apollo/client";
import { VinDecodedData } from "./vinDecoder";

export interface VehicleCatalogRow {
  sourceId: string;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  trimDescription: string | null;
  bodyStyle: string | null;
  driveType: string | null;
  fuelType: string | null;
  engine: string | null;
  transmission: string | null;
  doors: number | null;
}

export const GET_CATALOG_ROWS_BY_YMM = gql`
  query GetCatalogRowsByYmm($year: Int!, $make: String!, $model: String!) {
    allVehicleCatalogs(
      condition: { year: $year, make: $make, model: $model }
      orderBy: TRIM_ASC
      first: 200
    ) {
      nodes {
        sourceId
        year
        make
        model
        trim
        trimDescription
        bodyStyle
        driveType
        fuelType
        engine
        transmission
        doors
      }
    }
  }
`;

/**
 * Stage 3 fallback: pull all catalog rows for a year+make so we can
 * post-filter in JS by token-fuzzy match against trim/trim_description.
 * Bounded at 1000 because the largest single year+make slice in the
 * Teoalida dataset (Ford 2020) sits around 600 rows.
 */
export const GET_CATALOG_ROWS_BY_YM = gql`
  query GetCatalogRowsByYm($year: Int!, $make: String!) {
    allVehicleCatalogs(
      condition: { year: $year, make: $make }
      orderBy: MODEL_ASC
      first: 1000
    ) {
      nodes {
        sourceId
        year
        make
        model
        trim
        trimDescription
        bodyStyle
        driveType
        fuelType
        engine
        transmission
        doors
      }
    }
  }
`;

// ---- Normalization (mirrors server/src/services/vehicleCatalogMatcher.js) --

const MAKE_ALIASES: Record<string, string> = {
  "mercedes-benz": "Mercedes-Benz",
  "mercedes benz": "Mercedes-Benz",
  benz: "Mercedes-Benz",
  bmw: "BMW",
  chevy: "Chevrolet",
  vw: "Volkswagen",
  "rolls royce": "Rolls-Royce",
};

const MODEL_ALIASES: Record<string, string> = {
  "cr v": "CR-V",
  "cr-v": "CR-V",
  crv: "CR-V",
  "hr v": "HR-V",
  "hr-v": "HR-V",
  "1 series": "1 Series",
  "1-series": "1 Series",
  "2 series": "2 Series",
  "2-series": "2 Series",
  "3 series": "3 Series",
  "3-series": "3 Series",
  "4 series": "4 Series",
  "4-series": "4 Series",
  "5 series": "5 Series",
  "5-series": "5 Series",
  "6 series": "6 Series",
  "6-series": "6 Series",
  "7 series": "7 Series",
  "7-series": "7 Series",
  "8 series": "8 Series",
  "8-series": "8 Series",
  "f 150": "F-150",
  f150: "F-150",
  "f-150": "F-150",
  "f 250": "F-250",
  f250: "F-250",
  "f-250": "F-250",
  "f 350": "F-350",
  f350: "F-350",
  "f-350": "F-350",
};

function clean(s: string | null | undefined): string {
  if (s == null) return "";
  return String(s).replace(/\s+/g, " ").trim();
}

export function normalizeMake(raw: string | null | undefined): string {
  const c = clean(raw);
  if (!c) return "";
  const key = c.toLowerCase();
  if (MAKE_ALIASES[key]) return MAKE_ALIASES[key];
  return c
    .toLowerCase()
    .split(" ")
    .map((w) => (w.length <= 3 && w === w.toUpperCase() ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(" ");
}

export function normalizeModel(raw: string | null | undefined): string {
  const c = clean(raw);
  if (!c) return "";
  const key = c.toLowerCase();
  if (MODEL_ALIASES[key]) return MODEL_ALIASES[key];
  const m = c.match(/^([A-Za-z])\s+(\d+)(.*)$/);
  if (m) return `${m[1].toUpperCase()}-${m[2]}${m[3]}`;
  return c;
}

function normalizeDriveType(d: string | null | undefined): string {
  const s = clean(d).toLowerCase();
  if (!s) return "";
  if (/all\s*wheel|awd/.test(s)) return "AWD";
  if (/4wd|4x4|four\s*wheel/.test(s)) return "4WD";
  if (/rwd|rear\s*wheel/.test(s)) return "RWD";
  if (/fwd|front\s*wheel/.test(s)) return "FWD";
  return s.toUpperCase();
}

function normalizeFuelType(f: string | null | undefined): string {
  const s = clean(f).toLowerCase();
  if (!s) return "";
  if (/plug.?in/.test(s)) return "Plug-in Hybrid";
  if (/hybrid/.test(s)) return "Hybrid";
  if (/diesel/.test(s)) return "Diesel";
  if (/electric/.test(s) && !/gas/.test(s)) return "Electric";
  if (/gas|gasoline|petrol/.test(s)) return "Gasoline";
  return s;
}

function tokenize(s: string): string[] {
  return clean(s)
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

function tokenOverlap(a: string, b: string): number {
  const at = new Set(tokenize(a));
  const bt = new Set(tokenize(b));
  if (at.size === 0 || bt.size === 0) return 0;
  let hit = 0;
  at.forEach((t) => {
    if (bt.has(t)) hit++;
  });
  return hit / Math.max(at.size, bt.size);
}

export interface CatalogMatchResult {
  best: VehicleCatalogRow | null;
  confidence: number;
  candidates: Array<{ row: VehicleCatalogRow; score: number }>;
  /** Whether `best` cleared the auto-select confidence bar. */
  autoSelect: boolean;
}

/**
 * Confidence threshold for auto-selecting a catalog row in the wizard.
 * Below this, the dropdown is presented unselected so the user makes
 * the call. Tuned against the M340i/F-150 fixtures: a real "trim hit"
 * with matching drive/engine lands well above 0.5.
 */
export const AUTO_SELECT_CONFIDENCE = 0.5;

/** Score a single trim signal against a candidate row's trim. */
function trimScoreFor(signal: string, rowTrim: string | null): number {
  if (!signal || !rowTrim) return 0;
  const a = signal.toLowerCase();
  const b = rowTrim.toLowerCase();
  if (a === b) return 5;
  if (b.startsWith(a) || a.startsWith(b)) return 3;
  const o = tokenOverlap(signal, rowTrim);
  if (o >= 0.5) return 2;
  if (o > 0) return 1;
  return 0;
}

/**
 * Score each candidate against the VIN decode and pick the best.
 * Returns all candidates so the UI can populate a trim dropdown.
 *
 * Trim-axis fallbacks (in order): `trim` → `trim2` → `series2` → `series`,
 * plus the decoded `model` token when it differs from the catalog model line
 * (NHTSA stores the trim in Model for some makes, e.g. BMW "328i").
 * NHTSA frequently leaves Trim empty (every F-150 we saw) but populates
 * Series with a useful variant token (e.g. "Lariat", "King Ranch").
 */
export function matchCatalog(
  decode: Pick<
    VinDecodedData,
    | "model"
    | "trim"
    | "trim2"
    | "series"
    | "series2"
    | "bodyStyle"
    | "driveType"
    | "fuelType"
    | "engineCylinders"
    | "engineDisplacement"
    | "doors"
  >,
  rows: VehicleCatalogRow[]
): CatalogMatchResult {
  if (!rows || rows.length === 0) {
    return { best: null, confidence: 0, candidates: [], autoSelect: false };
  }

  const trimSignal =
    clean(decode.trim) ||
    clean(decode.trim2) ||
    clean(decode.series2) ||
    clean(decode.series);
  // NHTSA sometimes stores the actual trim in the *Model* field while the model
  // line lives in Series — e.g. BMW VINs decode as Model="328i", Series="3
  // Series", Trim="SA". In that case rows were matched via the series stage and
  // the decoded model token ("328i") is the real trim signal. We add it as a
  // secondary signal, used only when it differs from the row's model line so it
  // never interferes with the normal case (where Model is the model line).
  const modelSignal = clean(decode.model);
  const decodeDrive = normalizeDriveType(decode.driveType);
  const decodeFuel = normalizeFuelType(decode.fuelType);
  const decodeBody = clean(decode.bodyStyle).toLowerCase();
  const cyl = parseInt(String(decode.engineCylinders || ""), 10) || null;
  const disp = parseFloat(String(decode.engineDisplacement || "")) || null;
  const decodedDoors = parseInt(String(decode.doors || ""), 10) || null;

  const scored = rows.map((row) => {
    let score = 0;
    const modelIsTrimSignal =
      !!modelSignal &&
      !!row.model &&
      modelSignal.toLowerCase() !== row.model.toLowerCase();
    const trimScore = Math.max(
      trimScoreFor(trimSignal, row.trim),
      modelIsTrimSignal ? trimScoreFor(modelSignal, row.trim) : 0
    );
    score += trimScore;
    // Even when row.trim is empty, the trim_description often contains the
    // signal token (e.g. "4dr SuperCrew Lariat 4WD"). Use it as a tiebreaker.
    if (row.trimDescription) {
      const o = Math.max(
        tokenOverlap(trimSignal, row.trimDescription),
        modelIsTrimSignal ? tokenOverlap(modelSignal, row.trimDescription) : 0
      );
      if (o > 0) score += 1;
    }
    const rDrive = normalizeDriveType(row.driveType || "");
    if (decodeDrive && rDrive && decodeDrive === rDrive) score += 2;
    const rFuel = normalizeFuelType(row.fuelType || "");
    if (decodeFuel && rFuel && decodeFuel === rFuel) score += 1;
    if (row.engine) {
      const e = row.engine.toLowerCase();
      if (cyl && new RegExp(`\\b${cyl}\\s*cyl\\b`).test(e)) score += 1;
      if (disp) {
        const m = e.match(/(\d+\.\d+)\s*l\b/);
        if (m && Math.abs(parseFloat(m[1]) - disp) <= 0.15) score += 1;
      }
    }
    if (decodeBody && row.bodyStyle && decodeBody.includes(row.bodyStyle.toLowerCase())) {
      score += 1;
    }
    if (decodedDoors && row.doors && decodedDoors === row.doors) {
      score += 1;
    }
    return { row, score };
  });

  scored.sort((a, b) => b.score - a.score);
  const best = scored[0];
  const confidence = Math.min(1, best.score / 10);
  return {
    best: best.row,
    confidence,
    candidates: scored,
    autoSelect: confidence >= AUTO_SELECT_CONFIDENCE,
  };
}

/** Build a disambiguated label for a trim dropdown option. */
export function formatTrimOptionLabel(row: VehicleCatalogRow): string {
  const parts: string[] = [];
  parts.push(row.trim && row.trim.trim() ? row.trim : "(Base)");
  const extra: string[] = [];
  if (row.driveType) extra.push(row.driveType);
  if (row.engine) extra.push(row.engine);
  if (extra.length) parts.push(`— ${extra.join(", ")}`);
  return parts.join(" ");
}

export type CatalogLookupStage = "model" | "series" | "fuzzy" | "none";

export interface CatalogLookupResult {
  rows: VehicleCatalogRow[];
  stage: CatalogLookupStage;
}

/** Stage 3 post-filter: keep year+make rows whose trim/trim_description
 *  shares a non-trivial token with the decoded model. Catches taxonomy
 *  mismatches like NHTSA "C 300" vs catalog "C-Class" with trim "C300". */
function fuzzyFilterByModel(
  rows: VehicleCatalogRow[],
  model: string
): VehicleCatalogRow[] {
  const tokens = tokenize(model).filter((t) => t.length >= 2);
  if (tokens.length === 0) return [];
  return rows.filter((row) => {
    const haystack = `${row.trim || ""} ${row.trimDescription || ""} ${row.model || ""}`.toLowerCase();
    return tokens.some((t) => haystack.includes(t));
  });
}

/**
 * React hook: fetch catalog rows for a given decode using a 3-stage cascade:
 * 1. year + make + normalizedModel
 * 2. year + make + normalizedSeries (when Stage 1 returns 0)
 * 3. year + make, then JS fuzzy filter on the decoded model token (when 1+2 return 0)
 *
 * The stage that produced the rows is reported so the UI can label things
 * (e.g. show a "matched via platform" hint) and so telemetry can record
 * which fallback was needed.
 */
export function useCatalogRows(
  year: number | null,
  make: string,
  model: string,
  series?: string | null
): CatalogLookupResult & { loading: boolean; error: unknown } {
  const normMake = normalizeMake(make);
  const normModel = normalizeModel(model);
  const normSeries = normalizeModel(series || "");

  const skipStage1 = !year || !normMake || !normModel;
  const stage1 = useQuery(GET_CATALOG_ROWS_BY_YMM, {
    variables: { year, make: normMake, model: normModel },
    skip: skipStage1,
    fetchPolicy: "cache-first",
  });
  const stage1Rows: VehicleCatalogRow[] =
    (stage1.data && stage1.data.allVehicleCatalogs && stage1.data.allVehicleCatalogs.nodes) || [];

  const skipStage2 =
    skipStage1 ||
    stage1.loading ||
    !normSeries ||
    normSeries === normModel ||
    stage1Rows.length > 0;
  const stage2 = useQuery(GET_CATALOG_ROWS_BY_YMM, {
    variables: { year, make: normMake, model: normSeries },
    skip: skipStage2,
    fetchPolicy: "cache-first",
  });
  const stage2Rows: VehicleCatalogRow[] =
    (stage2.data && stage2.data.allVehicleCatalogs && stage2.data.allVehicleCatalogs.nodes) || [];

  const skipStage3 =
    skipStage1 ||
    stage1.loading ||
    stage2.loading ||
    stage1Rows.length > 0 ||
    stage2Rows.length > 0;
  const stage3 = useQuery(GET_CATALOG_ROWS_BY_YM, {
    variables: { year, make: normMake },
    skip: skipStage3,
    fetchPolicy: "cache-first",
  });
  const stage3All: VehicleCatalogRow[] =
    (stage3.data && stage3.data.allVehicleCatalogs && stage3.data.allVehicleCatalogs.nodes) || [];
  const stage3Rows = skipStage3 ? [] : fuzzyFilterByModel(stage3All, normModel);

  const loading =
    (!skipStage1 && stage1.loading) ||
    (!skipStage2 && stage2.loading) ||
    (!skipStage3 && stage3.loading);
  const error = stage1.error || stage2.error || stage3.error;

  let rows: VehicleCatalogRow[] = [];
  let stage: CatalogLookupStage = "none";
  if (stage1Rows.length > 0) {
    rows = stage1Rows;
    stage = "model";
  } else if (stage2Rows.length > 0) {
    rows = stage2Rows;
    stage = "series";
  } else if (stage3Rows.length > 0) {
    rows = stage3Rows;
    stage = "fuzzy";
  }

  return { rows, stage, loading: !!loading && !skipStage1, error };
}

/** Imperative fetch (single-stage, year+make+model). Kept for callers that
 *  already know the catalog has the row. New code should prefer the
 *  hook above so they get the multi-stage cascade. */
export async function fetchCatalogRows(
  client: ReturnType<typeof useApolloClient>,
  year: number,
  make: string,
  model: string
): Promise<VehicleCatalogRow[]> {
  const res = await client.query({
    query: GET_CATALOG_ROWS_BY_YMM,
    variables: { year, make: normalizeMake(make), model: normalizeModel(model) },
    fetchPolicy: "cache-first",
  });
  return (
    (res.data && res.data.allVehicleCatalogs && res.data.allVehicleCatalogs.nodes) || []
  );
}
